import React, { useEffect, useMemo, useState } from 'react';
import { Card } from 'primereact/card';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { Message } from 'primereact/message';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Absence } from '../types';
import { absenceAPI } from '../services/api';
import { showCustomAlert } from './CustomAlert';

const monthOptions = [
  { label: 'Januar', value: 0 },
  { label: 'Februar', value: 1 },
  { label: 'Mart', value: 2 },
  { label: 'April', value: 3 },
  { label: 'Maj', value: 4 },
  { label: 'Jun', value: 5 },
  { label: 'Jul', value: 6 },
  { label: 'Avgust', value: 7 },
  { label: 'Septembar', value: 8 },
  { label: 'Oktobar', value: 9 },
  { label: 'Novembar', value: 10 },
  { label: 'Decembar', value: 11 }
];

const dayOptions = Array.from({ length: 31 }).map((_, i) => ({ label: String(i + 1), value: i + 1 }));

function buildIso(year: number, month: number, day: number): string {
  const mm = String(month + 1).padStart(2, '0');
  const dd = String(day).padStart(2, '0');
  return `${year}-${mm}-${dd}`;
}

const Holidays: React.FC = () => {
  const [items, setItems] = useState<Absence[]>([]);
  const [loading, setLoading] = useState(false);
  const [showInactive, setShowInactive] = useState(false);

  const [modalVisible, setModalVisible] = useState(false);
  const [editing, setEditing] = useState<Absence | null>(null);
  const [error, setError] = useState('');

  const [form, setForm] = useState<{
    name: string;
    isRange: boolean;
    startMonth: number;
    startDay: number;
    endMonth: number;
    endDay: number;
    description: string;
  }>({
    name: '',
    isRange: false,
    startMonth: 0,
    startDay: 1,
    endMonth: 0,
    endDay: 1,
    description: ''
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const data = await absenceAPI.getAll(showInactive ? undefined : { isActive: true });
      setItems(data.filter((a) => a.category === 'holiday'));
    } catch (e: any) {
      console.error('Error fetching holidays:', e);
      showCustomAlert.error(e?.response?.data?.message || 'Greška pri učitavanju praznika');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInactive]);

  const openCreate = () => {
    setEditing(null);
    setError('');
    setForm({
      name: '',
      isRange: false,
      startMonth: 0,
      startDay: 1,
      endMonth: 0,
      endDay: 1,
      description: ''
    });
    setModalVisible(true);
  };

  const openEdit = (x: Absence) => {
    setEditing(x);
    setError('');

    const startMonth = x.isRecurring && x.recurringMonth !== undefined ? x.recurringMonth : 0;
    const startDay = x.isRecurring && x.recurringDay !== undefined ? x.recurringDay : 1;
    const endMonth =
      x.isRecurring && x.recurringEndMonth !== undefined ? x.recurringEndMonth : startMonth;
    const endDay =
      x.isRecurring && x.recurringEndDay !== undefined ? x.recurringEndDay : startDay;

    const isRange = !!(x.isRecurring && x.recurringEndMonth !== undefined && x.recurringEndDay !== undefined && (endMonth !== startMonth || endDay !== startDay));

    setForm({
      name: x.name || '',
      isRange,
      startMonth,
      startDay,
      endMonth,
      endDay,
      description: x.description || ''
    });
    setModalVisible(true);
  };

  const submit = async () => {
    try {
      setError('');
      if (!form.name.trim()) {
        setError('Naziv državnog praznika je obavezan.');
        return;
      }

      // Basic range validation (same-year mapping)
      if (form.isRange) {
        const startKey = form.startMonth * 100 + form.startDay;
        const endKey = form.endMonth * 100 + form.endDay;
        if (endKey < startKey) {
          setError('Datum završetka mora biti nakon datuma početka.');
          return;
        }
      }

      const year = new Date().getFullYear();
      const startDate = buildIso(year, form.startMonth, form.startDay);
      const endDate = buildIso(year, form.isRange ? form.endMonth : form.startMonth, form.isRange ? form.endDay : form.startDay);

      const payload = {
        name: form.name.trim(),
        category: 'holiday' as const,
        type: 'paid' as const, // required by backend schema; holidays behave as 8h
        startDate,
        endDate,
        appliesToAll: true,
        isRange: form.isRange,
        description: form.description.trim() || undefined,
        isRecurring: true,
        recurringMonth: form.startMonth,
        recurringDay: form.startDay,
        recurringEndMonth: form.isRange ? form.endMonth : undefined,
        recurringEndDay: form.isRange ? form.endDay : undefined
      };

      if (editing) {
        await absenceAPI.update(editing._id, payload);
        showCustomAlert.success('Praznik je ažuriran');
      } else {
        await absenceAPI.create(payload);
        showCustomAlert.success('Praznik je dodat');
      }

      setModalVisible(false);
      fetchData();
    } catch (e: any) {
      console.error('Error saving holiday:', e);
      setError(e?.response?.data?.message || 'Greška pri čuvanju praznika');
    }
  };

  const toggleActive = (x: Absence) => {
    confirmDialog({
      header: 'Potvrda',
      icon: 'pi pi-exclamation-triangle',
      message: x.isActive ? 'Deaktivirati praznik?' : 'Aktivirati praznik?',
      acceptLabel: x.isActive ? 'Deaktiviraj' : 'Aktiviraj',
      rejectLabel: 'Otkaži',
      accept: async () => {
        try {
          await absenceAPI.toggle(x._id);
          fetchData();
        } catch (e: any) {
          showCustomAlert.error(e?.response?.data?.message || 'Greška pri promeni statusa');
        }
      }
    });
  };

  const deleteHoliday = (x: Absence) => {
    confirmDialog({
      header: 'Brisanje',
      icon: 'pi pi-exclamation-triangle',
      message: `Da li ste sigurni da želite da obrišete praznik "${x.name}"?`,
      acceptLabel: 'Obriši',
      rejectLabel: 'Otkaži',
      acceptClassName: 'p-button-danger',
      accept: async () => {
        try {
          await absenceAPI.delete(x._id);
          showCustomAlert.success('Praznik je obrisan');
          fetchData();
        } catch (e: any) {
          showCustomAlert.error(e?.response?.data?.message || 'Greška pri brisanju praznika');
        }
      }
    });
  };

  const statusTemplate = (x: Absence) => {
    return (
      <span className={`inline-flex px-3 py-1 text-white text-sm border-round ${x.isActive ? 'bg-green-500' : 'bg-gray-500'}`}>
        {x.isActive ? 'Aktivan' : 'Neaktivan'}
      </span>
    );
  };

  const dateTemplate = (x: Absence) => {
    if (x.isRecurring && x.recurringMonth !== undefined && x.recurringDay !== undefined) {
      const start = `${x.recurringDay}. ${monthOptions[x.recurringMonth]?.label || ''}`;
      if (x.recurringEndMonth !== undefined && x.recurringEndDay !== undefined) {
        const end = `${x.recurringEndDay}. ${monthOptions[x.recurringEndMonth]?.label || ''}`;
        if (end !== start) return `${start} - ${end}`;
      }
      return start;
    }
    // fallback (non-recurring)
    try {
      const d = new Date(x.startDate);
      return `${d.getDate()}. ${monthOptions[d.getMonth()]?.label || ''}`;
    } catch {
      return '';
    }
  };

  const actionsTemplate = (x: Absence) => {
    return (
      <div className="flex gap-2 justify-content-end">
        <Button
          icon="pi pi-pencil"
          rounded
          text
          severity="info"
          tooltip="Izmeni"
          tooltipOptions={{ position: 'left' }}
          onClick={() => openEdit(x)}
        />
        <Button
          icon={x.isActive ? 'pi pi-eye-slash' : 'pi pi-eye'}
          rounded
          text
          severity="info"
          tooltip={x.isActive ? 'Deaktiviraj' : 'Aktiviraj'}
          tooltipOptions={{ position: 'left' }}
          onClick={() => toggleActive(x)}
        />
        <Button
          icon="pi pi-trash"
          rounded
          text
          severity="danger"
          tooltip="Obriši"
          tooltipOptions={{ position: 'left' }}
          onClick={() => deleteHoliday(x)}
        />
      </div>
    );
  };

  const headerRight = useMemo(() => {
    return (
      <div className="flex align-items-center gap-3">
        <Button label="Novi državni praznik" icon="pi pi-plus" severity="success" onClick={openCreate} />
        <div className="flex align-items-center gap-2">
          <Checkbox inputId="show-inactive" checked={showInactive} onChange={(e) => setShowInactive(!!e.checked)} />
          <label htmlFor="show-inactive">Prikaži neaktivne</label>
        </div>
      </div>
    );
  }, [showInactive]);

  return (
    <div className="p-4">
      <Card>
        <div className="flex justify-content-between align-items-center mb-3">
          <h2 className="m-0">Državni praznici</h2>
        </div>

        <div className="mb-3 p-3 border-1 border-round surface-border bg-white flex justify-content-between align-items-center">
          {headerRight}
          <div />
        </div>

        <DataTable
          value={items}
          loading={loading}
          paginator
          rows={10}
          rowsPerPageOptions={[10, 25, 50]}
          emptyMessage="Nema praznika"
          className="p-datatable-sm"
          responsiveLayout="stack"
          breakpoint="768px"
        >
          <Column field="name" header="Naziv" />
          <Column header="Datum" body={dateTemplate} />
          <Column header="Status" body={statusTemplate} />
          <Column header="Akcije" body={actionsTemplate} style={{ width: '180px' }} />
        </DataTable>
      </Card>

      <Dialog
        header={editing ? 'Izmeni državni praznik' : 'Novi državni praznik'}
        visible={modalVisible}
        onHide={() => setModalVisible(false)}
        style={{ width: '720px' }}
        draggable={false}
        className="p-fluid"
      >
        {error && <Message severity="error" text={error} className="mb-3" />}

        <div className="field">
          <label className="block mb-2">
            Naziv državnog praznika <span className="text-red-500">*</span>
          </label>
          <InputText value={form.name} placeholder="npr. Dan državnosti" onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </div>

        <div className="field flex align-items-center gap-2">
          <Checkbox
            inputId="range"
            checked={form.isRange}
            onChange={(e) => {
              const checked = !!e.checked;
              setForm((prev) => ({
                ...prev,
                isRange: checked,
                endMonth: checked ? prev.endMonth : prev.startMonth,
                endDay: checked ? prev.endDay : prev.startDay
              }));
            }}
          />
          <label htmlFor="range">Višednevni praznik (range)</label>
        </div>
        <small className="text-600 block mb-3">
          Unesite samo mesec i dan praznika (jednodnevni)
        </small>

        <div className="grid">
          <div className="col-12 md:col-6">
            <div className="field">
              <label className="block mb-2">
                Mesec početka <span className="text-red-500">*</span>
              </label>
              <Dropdown
                value={form.startMonth}
                options={monthOptions}
                onChange={(e) => setForm((prev) => ({
                  ...prev,
                  startMonth: e.value,
                  endMonth: prev.isRange ? prev.endMonth : e.value
                }))}
                appendTo="self"
              />
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="field">
              <label className="block mb-2">
                Dan početka <span className="text-red-500">*</span>
              </label>
              <Dropdown
                value={form.startDay}
                options={dayOptions}
                onChange={(e) => setForm((prev) => ({
                  ...prev,
                  startDay: e.value,
                  endDay: prev.isRange ? prev.endDay : e.value
                }))}
                appendTo="self"
              />
            </div>
          </div>
        </div>

        <div className="grid">
          <div className="col-12 md:col-6">
            <div className="field">
              <label className="block mb-2">Mesec završetka (isti kao početni)</label>
              <Dropdown
                value={form.isRange ? form.endMonth : form.startMonth}
                options={monthOptions}
                disabled={!form.isRange}
                onChange={(e) => setForm({ ...form, endMonth: e.value })}
                appendTo="self"
              />
            </div>
          </div>
          <div className="col-12 md:col-6">
            <div className="field">
              <label className="block mb-2">Dan završetka (isti kao početni)</label>
              <Dropdown
                value={form.isRange ? form.endDay : form.startDay}
                options={dayOptions}
                disabled={!form.isRange}
                onChange={(e) => setForm({ ...form, endDay: e.value })}
                appendTo="self"
              />
            </div>
          </div>
        </div>

        <div className="field">
          <label className="block mb-2">Opis</label>
          <InputTextarea value={form.description} placeholder="Opcioni opis" rows={3} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>

        <div className="flex justify-content-end gap-2 mt-2">
          <Button label="Otkaži" className="p-button-text" onClick={() => setModalVisible(false)} />
          <Button label="Sačuvaj" icon="pi pi-check" severity="info" onClick={submit} />
        </div>
      </Dialog>

      <ConfirmDialog />
    </div>
  );
};

export default Holidays;


